import { Helmet } from 'react-helmet';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const Languages = () => {
  return (
    <>
      <Helmet>
        <title>Programming Languages | InvizHer - Learn JavaScript, Python, Java & More</title>
        <meta name="description" content="Master popular programming languages including JavaScript, Python, Java, and C++. Comprehensive tutorials, guides, and best practices for developers." />
        <meta name="keywords" content="programming languages, JavaScript, Python, Java, C++, coding tutorials, learn programming" />
        <link rel="canonical" href="https://invizher.com/languages" />
        
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://invizher.com/languages" />
        <meta property="og:title" content="Programming Languages | InvizHer" />
        <meta property="og:description" content="Master popular programming languages with comprehensive tutorials and guides." />
        <meta property="og:site_name" content="InvizHer" />
        
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Programming Languages | InvizHer" />
        <meta name="twitter:description" content="Master JavaScript, Python, Java, and more." />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-32 pb-20" role="main">
          {/* Hero Section */}
          <section className="container mx-auto px-4 mt-8 mb-16">
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Master <span className="gradient-text">Programming Languages</span>
              </h1>
              <p className="text-gray-600 text-lg mb-8">
                Learn the most popular programming languages with comprehensive tutorials, guides, and best practices
              </p>
            </div>
          </section>

          {/* Languages Grid */}
          <section className="container mx-auto px-4">
            <div className="grid gap-6 grid-cols-1 md:grid-cols-2">
              {/* JavaScript */}
              <a href="/languages/javascript" className="glass-morphism rounded-2xl overflow-hidden hover:shadow-xl transition-all">
                <div className="p-6 md:p-8">
                  <div className="flex items-start justify-between mb-6">
                    <div className="bg-yellow-500/10 w-16 h-16 rounded-2xl flex items-center justify-center">
                      <i className="fab fa-js-square text-yellow-500 text-2xl"></i>
                    </div>
                    <span className="px-4 py-1 bg-yellow-500/10 text-yellow-500 rounded-full text-sm">
                      42 Tutorials
                    </span>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold mb-3">JavaScript</h3>
                  <p className="text-gray-600 mb-6">
                    Master the language of the web with modern ES6+ features, frameworks, and asynchronous programming.
                  </p>
                </div>
              </a>

              {/* Python */}
              <a href="/languages/python" className="glass-morphism rounded-2xl overflow-hidden hover:shadow-xl transition-all">
                <div className="p-6 md:p-8">
                  <div className="flex items-start justify-between mb-6">
                    <div className="bg-blue-500/10 w-16 h-16 rounded-2xl flex items-center justify-center">
                      <i className="fab fa-python text-blue-500 text-2xl"></i>
                    </div>
                    <span className="px-4 py-1 bg-blue-500/10 text-blue-500 rounded-full text-sm">
                      38 Tutorials
                    </span>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold mb-3">Python</h3>
                  <p className="text-gray-600 mb-6">
                    Explore versatile Python for web development, data science, AI, and automation with clear examples.
                  </p>
                </div>
              </a>

              {/* Java */}
              <a href="/languages/java" className="glass-morphism rounded-2xl overflow-hidden hover:shadow-xl transition-all">
                <div className="p-6 md:p-8">
                  <div className="flex items-start justify-between mb-6">
                    <div className="bg-red-500/10 w-16 h-16 rounded-2xl flex items-center justify-center">
                      <i className="fab fa-java text-red-500 text-2xl"></i>
                    </div>
                    <span className="px-4 py-1 bg-red-500/10 text-red-500 rounded-full text-sm">
                      28 Tutorials
                    </span>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold mb-3">Java</h3>
                  <p className="text-gray-600 mb-6">
                    Learn enterprise-level development with Java, covering OOP principles, Spring framework, and more.
                  </p>
                </div>
              </a>

              {/* C++ */}
              <a href="/languages/cpp" className="glass-morphism rounded-2xl overflow-hidden hover:shadow-xl transition-all">
                <div className="p-6 md:p-8">
                  <div className="flex items-start justify-between mb-6">
                    <div className="bg-purple-500/10 w-16 h-16 rounded-2xl flex items-center justify-center">
                      <i className="fas fa-code text-purple-500 text-2xl"></i>
                    </div>
                    <span className="px-4 py-1 bg-purple-500/10 text-purple-500 rounded-full text-sm">
                      22 Tutorials
                    </span>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold mb-3">C++</h3>
                  <p className="text-gray-600 mb-6">
                    Deep dive into system programming, game development, and high-performance applications with C++.
                  </p>
                </div>
              </a>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Languages;
